import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send } from "lucide-react";
import { messages as seedMsgs, conversations } from "@/data/mockData";
import { toast } from "sonner";

function initials(name) {
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Messages() {
  const [activeConv, setActiveConv] = useState(conversations[0].id);
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState(seedMsgs);

  const send = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setMsgs((m) => [
      ...m,
      {
        id: `m${m.length + 1}`,
        from: "You",
        role: "user",
        text: input.trim(),
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    ]);
    setInput("");
    toast.success("Message sent (demo)");
  };

  return (
    <div className="space-y-8" data-testid="messages-page">
      <PageHeader
        title="Messages & Notifications"
        subtitle="Communicate with clients and track important updates."
      />

      <Card className="border border-slate-200 dark:border-slate-800 rounded-xl elev-1 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr]" style={{ minHeight: "560px" }}>
          {/* Conversations sidebar */}
          <aside className="border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 dark:bg-slate-900/50" data-testid="conversation-list">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
              <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
                Conversations
              </div>
            </div>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`conv-${c.id}`}
                    onClick={() => setActiveConv(c.id)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white dark:bg-slate-900 transition-colors ${
                      activeConv === c.id ? "bg-white dark:bg-slate-900" : ""
                    }`}
                  >
                    <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-800">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                        {initials(c.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{c.name}</span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400 shrink-0">{c.time}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{c.preview}</span>
                        {c.unread > 0 && (
                          <span className="shrink-0 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center">
                            {c.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Chat area */}
          <section className="flex flex-col bg-white dark:bg-slate-900" data-testid="chat-area">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
              <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-800">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                  SP
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Support Portal</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Sample conversation</div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="message-thread">
              {msgs.map((m) => {
                const isUser = m.role === "user";
                const isSystem = m.role === "system";
                return (
                  <div
                    key={m.id}
                    data-testid={`msg-${m.id}`}
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-sm rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        isUser
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : isSystem
                          ? "bg-primary/10 dark:bg-primary/15 text-primary border border-primary/20 rounded-bl-sm"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-sm"
                      }`}
                    >
                      <div className="text-[10px] font-semibold opacity-70 mb-0.5 uppercase tracking-wider">
                        {m.from}
                      </div>
                      <div>{m.text}</div>
                      <div className={`mt-1 text-[10px] ${isUser ? "opacity-70" : "text-slate-500 dark:text-slate-400"}`}>
                        {m.time}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={send} className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2" data-testid="message-form">
              <Input
                data-testid="message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="h-10 rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
              <Button type="submit" data-testid="send-message-button" className="h-10 rounded-lg bg-primary hover:bg-primary/90">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </section>
        </div>
      </Card>
    </div>
  );
}
